'use client';

import { useState, useEffect, useMemo } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, MapPin, MessageSquare, ChevronRight, ChevronLeft, Globe } from 'lucide-react';
import { apiService } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import VPNWarning from './VPNWarning';
import { useTimezone } from '@/hooks/useTimezone';
import { formatTime } from '@/utils/timezone';
import EmailVerificationGate from './EmailVerificationGate';

// FormField type definition
export type FormFieldType = 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'number' | 'date' | 'checkbox' | 'radio';

export interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: any;
  options?: Array<{ value: string; label: string }>;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
}

interface ServiceType {
  id: string;
  name: string;
  description?: string;
  price?: string;
  priceOnRequest?: boolean;
  fields?: FormField[];
}

interface ServiceBookingFormProps {
  service: {
    id: number | string;
    name: string;
    price?: string;
    formFields?: FormField[];
    serviceTypes?: ServiceType[]; // Optional service types (e.g., horoscope types)
  };
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ServiceBookingForm({ service, onClose, onSuccess }: ServiceBookingFormProps) {
  const { user, isAuthenticated } = useAuth();
  const { timezoneData, vpnDetected, retry: retryTimezone } = useTimezone();
  const [step, setStep] = useState(1); // 1: Select service type, 2: Fill form
  const [selectedServiceType, setSelectedServiceType] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({
    fullName: user?.name || '',
    email: user?.email || '',
    contactNumber: user?.phone || '',
    address: '',
    message: '',
    serviceType: '', // Selected service type (e.g., "Canvas", "PDF")
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [showVpnWarning, setShowVpnWarning] = useState(vpnDetected);
  const [showVerificationGate, setShowVerificationGate] = useState(false);
  const [verificationPassed, setVerificationPassed] = useState(false);

  // Auto-detect service types from service name/features for common services
  const autoDetectedServiceTypes = useMemo(() => {
    const serviceName = (service.name || '').toLowerCase();
    
    // For horoscope making service
    if (serviceName.includes('horoscope') || serviceName.includes('making horoscope')) {
      return [
        {
          id: 'canvas',
          name: 'Premium Canvas Paper Horoscope',
          description: 'High-quality canvas paper horoscope for lasting preservation',
          fields: [
            { id: 'deliveryAddress', type: 'textarea', label: 'Delivery Address', name: 'deliveryAddress', required: true, placeholder: 'Enter your complete delivery address' },
            { id: 'deliveryDate', type: 'date', label: 'Preferred Delivery Date', name: 'deliveryDate', required: false },
            { id: 'birthDetails', type: 'textarea', label: 'Birth Details (Date, Time, Place)', name: 'birthDetails', required: true, placeholder: 'Date of Birth, Time, Birth Place' },
          ]
        },
        {
          id: 'flex',
          name: 'Flex Paper Horoscope (Waterproof)',
          description: 'Waterproof flex paper horoscope, perfect for durability',
          fields: [
            { id: 'deliveryAddress', type: 'textarea', label: 'Delivery Address', name: 'deliveryAddress', required: true, placeholder: 'Enter your complete delivery address' },
            { id: 'deliveryDate', type: 'date', label: 'Preferred Delivery Date', name: 'deliveryDate', required: false },
            { id: 'birthDetails', type: 'textarea', label: 'Birth Details (Date, Time, Place)', name: 'birthDetails', required: true, placeholder: 'Date of Birth, Time, Birth Place' },
          ]
        },
        {
          id: 'nepali',
          name: 'Nepali Traditional Paper Horoscope',
          description: 'Traditional Nepali paper format horoscope',
          fields: [
            { id: 'deliveryAddress', type: 'textarea', label: 'Delivery Address', name: 'deliveryAddress', required: true, placeholder: 'Enter your complete delivery address' },
            { id: 'deliveryDate', type: 'date', label: 'Preferred Delivery Date', name: 'deliveryDate', required: false },
            { id: 'birthDetails', type: 'textarea', label: 'Birth Details (Date, Time, Place)', name: 'birthDetails', required: true, placeholder: 'Date of Birth, Time, Birth Place' },
          ]
        },
        {
          id: 'pdf',
          name: 'Online PDF Horoscope',
          description: 'Digital PDF horoscope delivered via email',
          fields: [
            { id: 'birthDetails', type: 'textarea', label: 'Birth Details (Date, Time, Place)', name: 'birthDetails', required: true, placeholder: 'Date of Birth, Time, Birth Place' },
            { id: 'emailConfirmation', type: 'checkbox', label: 'Send to email address above', name: 'emailConfirmation', required: false },
          ]
        }
      ] as ServiceType[];
    }
    
    // For puja services
    if (serviceName.includes('puja')) {
      // Comprehensive puja types including 16 Sanskar
      const pujaTypeOptions = [
        'Rudri Puja',
        '16 Sanskar - Garbhadhan (Conception)',
        '16 Sanskar - Punsavan (Fetus Protection)',
        '16 Sanskar - Simantonayan (Baby Shower)',
        '16 Sanskar - Jatakarma (Birth Ceremony)',
        '16 Sanskar - Namakarana (Naming Ceremony / Nwaran)',
        '16 Sanskar - Nishkramana (First Outing)',
        '16 Sanskar - Annaprashana (First Food)',
        '16 Sanskar - Chudakarana (First Haircut)',
        '16 Sanskar - Karnavedha (Ear Piercing)',
        '16 Sanskar - Upanayana (Sacred Thread)',
        '16 Sanskar - Vedarambha (Education Start)',
        '16 Sanskar - Samavartana (Education Completion)',
        '16 Sanskar - Vivaha (Marriage)',
        '16 Sanskar - Antyeshti (Funeral / Last Rites)',
        'Griha Pravesh (House Warming)',
        'Marriage Ceremony',
        'Remedial Puja',
        'Satyanarayan Puja',
        'Lakshmi Puja',
        'Durga Puja',
        'Ganesh Puja',
        'Shiv Puja',
        'Vishnu Puja',
        'Navagraha Puja (Nine Planets)',
        'Other Puja'
      ];

      return [
        {
          id: 'home',
          name: 'Home Puja',
          description: 'Puja performed at your home',
          fields: [
            { id: 'pujaAddress', type: 'textarea', label: 'Home Address', name: 'pujaAddress', required: true, placeholder: 'Enter your home address where puja will be performed' },
            { id: 'preferredDate', type: 'date', label: 'Preferred Date', name: 'preferredDate', required: true },
            { id: 'preferredTime', type: 'time', label: 'Preferred Time', name: 'preferredTime', required: false },
            { id: 'pujaType', type: 'select', label: 'Type of Puja', name: 'pujaType', required: true, options: pujaTypeOptions },
            { id: 'numberOfPeople', type: 'number', label: 'Number of People Attending', name: 'numberOfPeople', required: false, placeholder: 'Approximate number' },
            { id: 'specialRequirements', type: 'textarea', label: 'Special Requirements', name: 'specialRequirements', required: false, placeholder: 'Any special requirements or instructions' },
          ]
        },
        {
          id: 'office',
          name: 'Swastishanti Office Puja',
          description: 'Puja performed at Swastishanti office',
          fields: [
            { id: 'preferredDate', type: 'date', label: 'Preferred Date', name: 'preferredDate', required: true },
            { id: 'preferredTime', type: 'time', label: 'Preferred Time', name: 'preferredTime', required: false },
            { id: 'pujaType', type: 'select', label: 'Type of Puja', name: 'pujaType', required: true, options: pujaTypeOptions },
            { id: 'numberOfPeople', type: 'number', label: 'Number of People Attending', name: 'numberOfPeople', required: false, placeholder: 'Approximate number' },
            { id: 'specialRequirements', type: 'textarea', label: 'Special Requirements', name: 'specialRequirements', required: false, placeholder: 'Any special requirements or instructions' },
          ]
        },
        {
          id: 'temple',
          name: 'Temple Puja',
          description: 'Puja performed at sacred temple',
          fields: [
            { id: 'templeName', type: 'text', label: 'Preferred Temple', name: 'templeName', required: false, placeholder: 'Name of temple (if any preference)' },
            { id: 'preferredDate', type: 'date', label: 'Preferred Date', name: 'preferredDate', required: true },
            { id: 'preferredTime', type: 'time', label: 'Preferred Time', name: 'preferredTime', required: false },
            { id: 'pujaType', type: 'select', label: 'Type of Puja', name: 'pujaType', required: true, options: pujaTypeOptions },
            { id: 'specialRequirements', type: 'textarea', label: 'Special Requirements', name: 'specialRequirements', required: false, placeholder: 'Any special requirements or instructions' },
          ]
        }
      ] as ServiceType[];
    }
    
    return null;
  }, [service.name]);

  // Parse service types from database or use auto-detected ones
  const parsedServiceTypes = useMemo(() => {
    if (service.serviceTypes && Array.isArray(service.serviceTypes) && service.serviceTypes.length > 0) {
      return service.serviceTypes;
    }
    return autoDetectedServiceTypes;
  }, [service.serviceTypes, autoDetectedServiceTypes]);

  // Use parsed service types
  const serviceTypes = parsedServiceTypes;

  // Default fields that are always included
  const defaultFields: FormField[] = [
    { id: 'fullName', type: 'text', label: 'Full Name', name: 'fullName', required: true },
    { id: 'email', type: 'email', label: 'Email', name: 'email', required: true },
    { id: 'contactNumber', type: 'tel', label: 'Contact Number', name: 'contactNumber', required: true },
    { id: 'address', type: 'textarea', label: 'Address', name: 'address', required: false },
    { id: 'message', type: 'textarea', label: 'Message/Notes', name: 'message', required: false },
  ];

  // Get fields for current step
  const currentFields = useMemo(() => {
    if (step === 1 && serviceTypes) {
      return []; // No form fields on step 1, just type selection
    }
    
    // Step 2: Show default fields + type-specific fields + custom fields
    let fields = [...defaultFields];
    
    if (selectedServiceType && serviceTypes) {
      const selectedType = serviceTypes.find(t => t.id === selectedServiceType);
      if (selectedType?.fields) {
        fields = [...fields, ...selectedType.fields];
      }
    }
    
    // Add custom form fields from service
    if (service.formFields) {
      fields = [...fields, ...service.formFields];
    }
    
    return fields;
  }, [step, selectedServiceType, serviceTypes, service.formFields]);

  // Check if we need to show service type selection
  const needsServiceTypeSelection = serviceTypes && serviceTypes.length > 0;

  // Initialize step based on whether service types are needed
  // Skip step 1 (service type selection) - go directly to form
  useEffect(() => {
    setStep(2);
  }, [needsServiceTypeSelection]);

  // Load time slots when service is available
  useEffect(() => {
    if (service.id && step === 2 && !vpnDetected) {
      loadTimeSlots();
    }
  }, [service.id, step, vpnDetected]);

  // Update VPN warning visibility
  useEffect(() => {
    setShowVpnWarning(vpnDetected);
  }, [vpnDetected]);

  const loadTimeSlots = async () => {
    try {
      setLoadingTimeSlots(true);
      const response: any = await apiService.getTimeSlots(service.id);
      if (response?.success && response?.data?.timeSlots) {
        setTimeSlots(response.data.timeSlots);
        if (response.data.vpnDetected) {
          setShowVpnWarning(true);
        }
      }
    } catch (err: any) {
      console.error('Failed to load time slots:', err);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  const handleChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceTypeSelect = (typeId: string) => {
    setSelectedServiceType(typeId);
    setFormData(prev => ({ ...prev, serviceType: typeId }));
    setStep(2);
  };

  const handleBack = () => {
    if (step === 2 && needsServiceTypeSelection) {
      setStep(1);
      setSelectedServiceType(null);
      setFormData(prev => ({ ...prev, serviceType: '' }));
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.name] || field.defaultValue || '';

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            id={field.id}
            name={field.name}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );

      case 'select':
        return (
          <select
            id={field.id}
            name={field.name}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            required={field.required}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Select {field.label}</option>
            {(field.options || []).map((option, index) => (
              <option key={index} value={typeof option === 'string' ? option : option.value}>
                {typeof option === 'string' ? option : option.label}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || []).map((option, index) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const optionLabel = typeof option === 'string' ? option : option.label;
              return (
                <label key={index} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={field.name}
                    value={optionValue}
                    checked={value === optionValue}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    required={field.required}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-gray-700 dark:text-gray-300">{optionLabel}</span>
                </label>
              );
            })}
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              id={field.id}
              name={field.name}
              checked={!!value}
              onChange={(e) => handleChange(field.name, e.target.checked)}
              required={field.required}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span className="text-gray-700 dark:text-gray-300">{field.label}</span>
          </label>
        );

      default:
        return (
          <input
            type={field.type}
            id={field.id}
            name={field.name}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            min={field.validation?.min}
            max={field.validation?.max}
            pattern={field.validation?.pattern}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );
    }
  };

  const submitServiceBooking = async () => {
    setError('');
    setSuccess(false);

    // Validate required fields
    const missingFields = currentFields
      .filter(f => f.required && !formData[f.name])
      .map(f => f.label);

    if (missingFields.length > 0) {
      setError(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Validate service type selection if needed
    if (needsServiceTypeSelection && !selectedServiceType) {
      setError('Please select a service type');
      return;
    }

    if (!user?.emailVerified && !verificationPassed) {
      setShowVerificationGate(true);
      return;
    }

    // Block submission if VPN is detected
    if (vpnDetected && showVpnWarning) {
      setError('Please disable your VPN to proceed with booking. This ensures accurate time slot conversion based on your location.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        serviceId: service.id,
        ...formData,
        // Include selected service type
        serviceType: selectedServiceType || formData.serviceType,
        // Include all custom fields
        customFields: [
          ...(service.formFields?.map(field => ({
            name: field.name,
            value: formData[field.name],
          })) || []),
          // Include type-specific fields
          ...(selectedServiceType && serviceTypes
            ? serviceTypes.find(t => t.id === selectedServiceType)?.fields?.map(field => ({
                name: field.name,
                value: formData[field.name],
              })) || []
            : [])
        ],
      };

      const response: any = await apiService.createServiceBooking(payload);

      if (response?.success) {
        setSuccess(true);
        setVerificationPassed(false);
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 2000);
      } else {
        setError(response?.message || 'Failed to book service');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to book service');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitServiceBooking();
  };

  if (success) {
    return (
      <>
        <EmailVerificationGate
          open={showVerificationGate}
          onClose={() => setShowVerificationGate(false)}
          onVerified={async () => {
            setVerificationPassed(true);
            await submitServiceBooking();
          }}
        />
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Booking Successful!</h3>
          <p className="text-gray-600 dark:text-gray-400">Your service request has been submitted. We'll contact you soon.</p>
        </div>
      </div>
      </>
    );
  }

  // Step 1: Service Type Selection
  const renderServiceTypeSelection = () => {
    if (!serviceTypes || serviceTypes.length === 0) return null;

    return (
      <div className="p-6 space-y-4">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Select {service.name} Type
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Choose the type of service you need
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {serviceTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => handleServiceTypeSelect(type.id)}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                selectedServiceType === type.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {type.name}
                  </h4>
                  {type.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {type.description}
                    </p>
                  )}
                  {type.price && !type.priceOnRequest && (
                    <p className="text-sm font-bold text-primary-600 dark:text-primary-400">
                      {type.price}
                    </p>
                  )}
                  {type.priceOnRequest && (
                    <p className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                      Price on Request
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl w-full max-w-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Book Service</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{service.name}</p>
            {service.price && !selectedServiceType && (
              <p className="text-lg font-bold text-primary-600 dark:text-primary-400 mt-2">
                {service.price}
              </p>
            )}
            {selectedServiceType && serviceTypes && (
              <p className="text-sm text-primary-600 dark:text-primary-400 mt-2">
                {serviceTypes.find(t => t.id === selectedServiceType)?.name}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Step Indicator - Hidden */}

        {/* Step 2: Form */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
                {error}
              </div>
            )}

            {currentFields.map((field) => (
              <div key={field.id}>
                <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderField(field)}
              </div>
            ))}

            {/* Time Slots Display */}
            {timeSlots.length > 0 && !vpnDetected && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  <Clock className="inline w-4 h-4 mr-2" />
                  Available Time Slots
                </label>
                <div className="space-y-2">
                  {timeSlots
                    .filter(slot => slot.isAvailable)
                    .map((slot, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <div>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {slot.day}
                            </span>
                            <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                              {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                            </span>
                            {slot.convertedTimezone && slot.originalTimezone && (
                              <span className="text-xs text-blue-600 dark:text-blue-400 ml-2">
                                (Converted from {slot.originalTimezone})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  {timeSlots.filter(slot => slot.isAvailable).length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No available time slots for this service
                    </p>
                  )}
                </div>
              </div>
            )}

            {loadingTimeSlots && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                Loading available time slots...
              </div>
            )}

            {!isAuthenticated && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 text-yellow-800 dark:text-yellow-200 text-sm">
                Please <a href="/login" className="underline font-semibold">login</a> to book services, or continue as guest.
              </div>
            )}

            <div className="flex gap-4 pt-4">
              {needsServiceTypeSelection && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : service.price ? 'Book Now' : 'Submit Request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

